import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Eye } from "lucide-react";
import { TeamMember } from "../ChangeRoleModal";
import { BASE_URL } from "../../api/url";
import { getNutritionistFullDetails } from "../../../services/team";

/* ── Shape of the data we actually render. The API returns parallel arrays
   for education/specialisation/expertise/certification, but the UI is laid
   out for single primary values, so we flatten with the first entry and
   keep the full lists for the multi-row sections (documents, etc). ── */
export interface NutritionistFullDetails {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profileUrl: string | null;
  dob: string;
  gender: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  workExperience: string;
  jobType: string;
  role: string;
  reportingManager: string;
  joiningDate: string;
  status: string;
  languages: string[];
  educations: { qualification: string; collegeName: string }[];
  specialisations: { specialisation: string; experience: string }[];
  expertises: string[];
  certifications: string[];
  documents: { type: string; number: string; uploadUrl: string | null }[];
  actionBadges: string[];
  contentBadges: string[];
}

interface ApiEducation { education_clg_name?: string; education_qualification?: string }
interface ApiSpecialisation { spec_exp?: string; specialisation?: string }
interface ApiExpertise { expertise?: string }
interface ApiCertification { certification_name?: string }
interface ApiDocument { doc_type?: string; doc_no?: string; doc_upload_url?: string | null }

type ApiPermissionGroup = Record<string, boolean | undefined>;
type ApiPermissions = Record<string, ApiPermissionGroup | undefined>;

interface ApiNutritionistData {
  nutrist_title?: string;
  nutrist_fname?: string;
  nutrist_lname?: string;
  nutrist_email?: string;
  nutrist_mobileno?: string;
  nutrist_profile?: string | null;
  nutrist_dob?: string;
  nutrist_gender?: string;
  nutrist_address?: string;
  nutrist_state?: string;
  nutrist_city?: string;
  nutrist_pincode?: string;
  nutrist_job_type?: string;
  nutrist_assign_role?: string;
  nutrist_workexperience?: string;
  nutrist_lang1?: string;
  nutrist_lang2?: string;
  nutrist_lang3?: string;
  nutrist_lang4?: string;
  nutrist_joining_date?: string;
  nutrist_reporting_manager?: string;
  nutrist_status?: string;
  nutrist_education_details?: ApiEducation[];
  nutrist_specialisation_details?: ApiSpecialisation[];
  nutrist_expertise_details?: ApiExpertise[];
  nutrist_certification?: ApiCertification[];
  nutrist_document_details?: ApiDocument[];
  role?: string;
  permissions?: ApiPermissions;
}

/* Known permission "<group>.<action>" keys mapped to friendly badge labels.
   Any granted permission whose key is not in this map falls back to a
   humanised version of the key so every API permission is still surfaced. */
const ACTION_PERMISSION_LABELS: Record<string, string> = {
  "admin.assign_roles":          "Assign roles",
  "admin.manage_nutritionists":  "Manage nutritionists",
  "admin.view_analytics":        "View analytics",
  "client.assign":               "Assign nutritionist",
  "client.manage":               "Manage clients",
  "client.view":                 "View clients",
  "client.view_all":             "View all clients",
  "consultation.conduct":        "Do consultations",
};

const CONTENT_OPERATIONS = new Set(["create", "delete", "edit", "view"]);

const humanize = (raw: string): string =>
  raw
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildPermissionBadges = (
  permissions: ApiPermissions | undefined,
): { actionBadges: string[]; contentBadges: string[] } => {
  const actionBadges: string[] = [];
  const contentBadges: string[] = [];
  if (!permissions) return { actionBadges, contentBadges };

  for (const [group, actions] of Object.entries(permissions)) {
    if (!actions) continue;

    const entries = Object.entries(actions);
    const isContentGroup =
      entries.length > 0 &&
      entries.every(([action]) => CONTENT_OPERATIONS.has(action));

    if (isContentGroup) {
      for (const [action, granted] of entries) {
        if (granted) contentBadges.push(`${humanize(group)} - ${humanize(action)}`);
      }
      continue;
    }

    for (const [action, granted] of entries) {
      if (!granted) continue;
      const label =
        ACTION_PERMISSION_LABELS[`${group}.${action}`] ??
        `${humanize(group)} - ${humanize(action)}`;
      actionBadges.push(label);
    }
  }
  return { actionBadges, contentBadges };
};

const buildProfileUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const mapApiToDetails = (d: ApiNutritionistData): NutritionistFullDetails => {
  const { actionBadges, contentBadges } = buildPermissionBadges(d.permissions);
  return {
    title:        d.nutrist_title  ?? "",
    firstName:    d.nutrist_fname  ?? "",
    lastName:     d.nutrist_lname  ?? "",
    email:        d.nutrist_email  ?? "",
    phone:        d.nutrist_mobileno ?? "",
    profileUrl:   buildProfileUrl(d.nutrist_profile),
    dob:          d.nutrist_dob    ?? "",
    gender:       d.nutrist_gender ?? "",
    address:      d.nutrist_address ?? "",
    city:         d.nutrist_city   ?? "",
    state:        d.nutrist_state  ?? "",
    pincode:      d.nutrist_pincode ?? "",
    workExperience: d.nutrist_workexperience ?? "",
    jobType:      d.nutrist_job_type ?? "",
    role:         d.role ?? d.nutrist_assign_role ?? "",
    reportingManager: d.nutrist_reporting_manager ?? "",
    joiningDate:  d.nutrist_joining_date ?? "",
    status:       d.nutrist_status ?? "",
    languages: [
      d.nutrist_lang1 ?? "",
      d.nutrist_lang2 ?? "",
      d.nutrist_lang3 ?? "",
      d.nutrist_lang4 ?? "",
    ],
    educations: (d.nutrist_education_details ?? []).map((e) => ({
      qualification: e.education_qualification ?? "",
      collegeName:   e.education_clg_name ?? "",
    })),
    specialisations: (d.nutrist_specialisation_details ?? []).map((s) => ({
      specialisation: s.specialisation ?? "",
      experience:     s.spec_exp ?? "",
    })),
    expertises: (d.nutrist_expertise_details ?? [])
      .map((e) => e.expertise ?? "")
      .filter(Boolean),
    certifications: (d.nutrist_certification ?? [])
      .map((c) => c.certification_name ?? "")
      .filter(Boolean),
    documents: (d.nutrist_document_details ?? []).map((doc) => ({
      type:      doc.doc_type ?? "",
      number:    doc.doc_no ?? "",
      uploadUrl: buildProfileUrl(doc.doc_upload_url ?? null),
    })),
    actionBadges,
    contentBadges,
  };
};

/* ── Read-only field ── */
const ViewField: React.FC<{
  label: string;
  value?: string;
  placeholder?: string;
  className?: string;
}> = ({ label, value, placeholder = "—", className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
      {label}
    </label>
    <div className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-[#F9F9F9] min-h-[38px] flex items-center">
      <span className={value ? "text-[#1F1F1F]" : "text-[#9E9E9E]"}>
        {value || placeholder}
      </span>
    </div>
  </div>
);

/* ── Main page ── */
const NutritionistViewPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { id }    = useParams();

  const member: TeamMember | undefined =
    (location.state as { member: TeamMember } | null)?.member;

  const [details, setDetails] = useState<NutritionistFullDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoadError("Missing nutritionist id");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const apiData = (await getNutritionistFullDetails(Number(id))) as ApiNutritionistData;
        if (cancelled) return;
        setDetails(mapApiToDetails(apiData ?? {}));
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Failed to load nutritionist",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const fallbackName = member?.name ?? `Nutritionist #${id}`;
  const displayName = details
    ? [details.firstName, details.lastName].filter(Boolean).join(" ") || fallbackName
    : fallbackName;

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      {/* ── Header ── */}
      <div className="flex-shrink-0 bg-white border-b border-[#E8E8E8] px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl 3xl:text-2xl font-semibold font-inter text-[#1F1F1F]">
              {displayName}
            </h1>
            <nav className="flex items-center gap-1 text-xs 3xl:text-sm font-inter text-[#646464] mt-0.5">
              <button
                onClick={() => navigate("/team")}
                className="hover:text-[#0DB5B5] transition-colors"
              >
                Team
              </button>
              <span>›</span>
              <span>View nutritionist</span>
            </nav>
          </div>
          <button
            type="button"
            disabled={!details}
            onClick={() =>
              navigate("/team/add-nutritionist", {
                state: { member, mode: "edit", details },
              })
            }
            className="flex items-center gap-1.5 px-4 py-2 border-[0.5px] border-[#20BCBB] rounded-[6px] text-sm 3xl:text-base font-medium font-inter text-[#20BCBB] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Edit details
          </button>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 custom-scrollbar-overlay">
        {loading ? (
          <div className="flex items-center justify-center h-full py-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-base text-gray-500 font-inter">Loading nutritionist</p>
            </div>
          </div>
        ) : loadError ? (
          <div className="flex items-center justify-center h-full py-16">
            <p className="text-red-500 text-sm sm:text-base 3xl:text-[18px] font-inter">
              {loadError}
            </p>
          </div>
        ) : details ? (
        <div className="space-y-4">

          {/* Personal details card */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Photo / avatar */}
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <div
                  className="w-36 h-40 border-2 border-dashed border-[#E0E0E0] rounded-lg overflow-hidden flex items-center justify-center bg-[#F9F9F9]"
                >
                  {details.profileUrl ? (
                    <img
                      src={details.profileUrl}
                      alt={displayName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const t = e.currentTarget;
                        t.style.display = "none";
                        const fb = t.nextElementSibling as HTMLElement | null;
                        if (fb) fb.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full flex-col items-center justify-center gap-1"
                    style={{ display: details.profileUrl ? "none" : "flex" }}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#E8E8E8] flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
                          stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="text-xs 3xl:text-sm text-[#9CA3AF] font-inter text-center px-2 leading-tight">
                      No image uploaded
                    </span>
                  </div>
                </div>
                <p className="text-[10px] 3xl:text-xs text-[#9CA3AF] font-inter">
                  JPG/PNG · Max 2 MB
                </p>
              </div>

              {/* Personal fields */}
              <div className="flex-1 grid grid-cols-1 gap-4">
                <ViewField label="Title" value={details.title || undefined} placeholder="eg. Mr" className="max-w-[200px]" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ViewField label="First name" value={details.firstName || undefined} placeholder="eg. John" />
                  <ViewField label="Last name"  value={details.lastName  || undefined} placeholder="eg. Doe"  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ViewField label="Phone number"  value={details.phone || undefined} placeholder="eg. 7869 369 854"  />
                  <ViewField label="Email address" value={details.email || undefined} placeholder="eg. john@gmail.com" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ViewField label="DOB"    value={details.dob    || undefined} placeholder="DD/MM/YYYY" />
                  <ViewField label="Gender" value={details.gender || undefined} placeholder="eg. Male" />
                </div>
              </div>
            </div>
          </div>

          {/* Address card */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
            <ViewField label="Address" value={details.address || undefined} placeholder="eg. 13D, MR road" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ViewField label="City"    value={details.city    || undefined} placeholder="eg. Mumbai"      />
              <ViewField label="State"   value={details.state   || undefined} placeholder="eg. Maharashtra" />
              <ViewField label="Pincode" value={details.pincode || undefined} placeholder="eg. 400001"      />
            </div>
          </div>

          {/* Documents card */}
          {details.documents.length > 0 && (
            <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
              {details.documents.map((doc, idx) => (
                <div key={`${doc.type}-${idx}`} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                      {doc.type || "Document"}
                    </label>
                    <div className="w-full h-10 px-3 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-[#F9F9F9] flex items-center">
                      <span className={doc.number ? "text-[#1F1F1F]" : "text-[#9E9E9E]"}>
                        {doc.number || "—"}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => doc.uploadUrl && window.open(doc.uploadUrl, "_blank", "noopener,noreferrer")}
                    disabled={!doc.uploadUrl}
                    className="flex items-center gap-1.5 px-4 h-10 border border-[#0DB5B5] text-[#0DB5B5] rounded-md text-sm 3xl:text-base font-medium font-inter hover:bg-[#E5F8F8] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  <div />
                </div>
              ))}
            </div>
          )}

          {/* Education card */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
            {(details.educations.length > 0 ? details.educations : [{ qualification: "", collegeName: "" }]).map((edu, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ViewField label="Education"   value={edu.qualification || undefined} placeholder="Select education qualification" />
                <ViewField label="College name" value={edu.collegeName  || undefined} placeholder="eg. JK College" />
              </div>
            ))}
          </div>

          {/* Work Experience + Specialisation card */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
            <ViewField
              label="Work Experience"
              value={details.workExperience || undefined}
              placeholder="eg. 3 years"
              className="max-w-sm"
            />
            <div className="border-t border-[#F3F4F6]" />
            {(details.specialisations.length > 0 ? details.specialisations : [{ specialisation: "", experience: "" }]).map((spec, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ViewField label="Specialisation" value={spec.specialisation || undefined} placeholder="Select specialisation" />
                <ViewField label="Experience"     value={spec.experience     || undefined} placeholder="eg. 1 year"            />
              </div>
            ))}
          </div>

          {/* Expertise & Certification — side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">
              <ViewField
                label="Expertise"
                value={details.expertises.join(", ") || undefined}
                placeholder="Select expertise"
              />
            </div>
            <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">
              <ViewField
                label="Certification"
                value={details.certifications.join(", ") || undefined}
                placeholder="Select certification"
              />
            </div>
          </div>

          {/* Language known card */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">
            <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] block mb-1.5">
              Language known
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter bg-[#F9F9F9] min-h-[38px] flex items-center"
                >
                  <span className={details.languages[i] ? "text-[#1F1F1F]" : "text-[#9E9E9E]"}>
                    {details.languages[i] || `Language ${i + 1}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Role & Reporting card */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ViewField label="Role"     value={details.role    || member?.designation || undefined} placeholder="Select role"      />
              <ViewField label="Job type" value={details.jobType || undefined}                         placeholder="Select job type"  />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             
              <ViewField label="Reporting manager name" value={details.reportingManager || undefined}                              placeholder="eg. Dipanwita Biswas"  />
             <ViewField
              label="Joining date"
              value={details.joiningDate || member?.joiningDate || undefined}
              placeholder="DD/MM/YY"
             
            />
            </div>
           
          </div>

          {/* Access & permissions card */}
          <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 mb-4">
            <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-0.5">
              Access &amp; permissions
            </h3>
            <p className="text-xs 3xl:text-sm font-inter text-[#646464] mb-4">
              These are granted automatically based on the selected role.
            </p>
            {details.actionBadges.length === 0 && details.contentBadges.length === 0 ? (
              <p className="text-sm font-inter text-[#9E9E9E]">
                No permissions granted for this role.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {[...details.actionBadges, ...details.contentBadges].map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#E5F8F8] text-[#0DB5B5] text-xs 3xl:text-sm font-medium font-inter"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            )}
          </div>

        </div>
        ) : null}
      </div>
    </div>
  );
};

export default NutritionistViewPage;
